// Evidence highlighting (#376): connect what the customer wrote with what the system read
// out of it, and with what the draft answers. Pure text segmentation — the workspace renders
// the segments; nothing here touches the DOM or invents evidence.
//
// Sources of truth:
//  - a fact's `evidenceText` is the literal quote the extractor anchored the fact to; it is
//    matched inside the message bodies (scoped by `source_message_id` when present);
//  - the draft naturally repeats fact VALUES (dates, counts, names); those are matched inside
//    the draft body so the operator sees which parts of the reply rest on which fact.
import type { Fact } from "../api/types";

export interface EvidenceMark {
  /** Stable identity — the fact's canonical field path. */
  key: string;
  /** The literal text to look for. */
  text: string;
  /** Fact status (drives the highlight tone via FACT_TONE). */
  status: string;
  /** Message the evidence came from; null = match in any inbound message. */
  sourceMessageId: string | null;
}

export interface SegmentMark {
  key: string;
  status: string;
}

export interface TextSegment {
  text: string;
  /** Field path of the mark shown at rest; undefined = plain text. */
  markKey?: string;
  status?: string;
  /** Every fact whose text covers this stretch, the resting one first; absent on plain text. */
  marks?: SegmentMark[];
}

/** Below this length a text is marked only where it stands as a token of its own (#2128). */
const MIN_MARK_LENGTH = 3;

/** Marks anchored to the customer's own words (evidenceText). */
export function evidenceMarks(facts: Fact[]): EvidenceMark[] {
  const marks: EvidenceMark[] = [];
  for (const f of facts) {
    const text = (f.evidenceText ?? "").trim();
    if (!text) continue;
    marks.push({
      key: f.fieldPath,
      text,
      status: f.status,
      sourceMessageId: f.source_message_id ?? null,
    });
  }
  return marks;
}

function valueStrings(value: unknown): string[] {
  if (value === null || value === undefined || typeof value === "boolean") return [];
  if (Array.isArray(value)) return value.flatMap(valueStrings);
  if (typeof value === "object") return [];
  const s = String(value).trim();
  // Skip trivially short values: matching "2" against a whole letter highlights noise.
  return s.length >= MIN_MARK_LENGTH ? [s] : [];
}

/** Marks for the draft: the facts' literal values as they would be echoed in the reply. */
export function draftValueMarks(facts: Fact[]): EvidenceMark[] {
  const marks: EvidenceMark[] = [];
  for (const f of facts) {
    for (const s of valueStrings(f.value)) {
      marks.push({ key: f.fieldPath, text: s, status: f.status, sourceMessageId: null });
    }
  }
  return marks;
}

interface Place {
  start: number;
  end: number;
}

interface Range extends Place {
  mark: EvidenceMark;
  order: number;
}

const patterns = new Map<string, RegExp | null>();
const PATTERN_ENTRIES = 2000;

/**
 * The literal text as a search: any letter case, and any run of whitespace where the text has
 * one, so a quote still matches where the message breaks the line inside it.
 */
function quotePattern(text: string): RegExp | null {
  const kept = patterns.get(text);
  if (kept !== undefined) return kept;
  const words = text.split(/\s+/u).filter(Boolean).map((word) => word.replace(/[\\^$.*+?()[\]{}|]/g, "\\$&"));
  const pattern = words.length ? new RegExp(words.join("\\s+"), "giu") : null;
  if (patterns.size >= PATTERN_ENTRIES) patterns.clear();
  patterns.set(text, pattern);
  return pattern;
}

const WORD = /[\p{L}\p{N}]/u;
const DIGIT = /\p{Nd}/u;
const NUMBER_JOINER = /[.,:/-]/;

/**
 * Whether the text ends at this edge of the occurrence. `inner` is the occurrence's own edge
 * character, `outer` the one just outside it and `beyond` the one after that. A digit joined to
 * another digit by punctuation is one number: the "04" of "19.04.2027", the "30" of "13:30".
 */
function edgeFree(inner: string, outer: string | undefined, beyond: string | undefined): boolean {
  if (outer === undefined || !WORD.test(inner)) return true;
  if (WORD.test(outer)) return false;
  return !(DIGIT.test(inner) && NUMBER_JOINER.test(outer) && beyond !== undefined && DIGIT.test(beyond));
}

function standsAlone(body: string, { start, end }: Place): boolean {
  return edgeFree(body[start], body[start - 1], body[start - 2])
    && edgeFree(body[end - 1], body[end], body[end + 1]);
}

/**
 * Where a mark's text lies in `body`: the first occurrence that stands alone, not inside a
 * longer word or number. A text of MIN_MARK_LENGTH or more falls back to its first occurrence;
 * a shorter one is left unmarked, because "50" inside a postcode is not the guest count.
 */
function placeMark(body: string, text: string): Place | null {
  const pattern = quotePattern(text);
  if (!pattern) return null;
  let first: Place | null = null;
  pattern.lastIndex = 0;
  for (let match = pattern.exec(body); match; match = pattern.exec(body)) {
    const place = { start: match.index, end: match.index + match[0].length };
    if (standsAlone(body, place)) return place;
    first ??= place;
    pattern.lastIndex = match.index + 1;
  }
  return text.length >= MIN_MARK_LENGTH ? first : null;
}

/**
 * Split `body` into plain and highlighted segments. Each mark is placed once (see placeMark).
 * Several facts may quote the same text, a part of another fact's quote, or overlapping
 * texts: the body is cut at every start and end, and a segment lists every fact that covers
 * it. The resting mark is the fact with the shortest text, then the earlier fact. Returns a
 * single plain segment when nothing matches.
 */
export function segmentText(body: string, marks: EvidenceMark[]): TextSegment[] {
  if (!body) return [];
  const ranges: Range[] = [];
  marks.forEach((mark, order) => {
    const place = placeMark(body, mark.text.trim());
    if (place) ranges.push({ ...place, mark, order });
  });
  if (!ranges.length) return [{ text: body }];
  ranges.sort((a, b) => (a.end - a.start) - (b.end - b.start) || a.order - b.order);
  const cuts = [...new Set([0, body.length, ...ranges.flatMap((r) => [r.start, r.end])])].sort((a, b) => a - b);
  const segments: TextSegment[] = [];
  for (let i = 0; i + 1 < cuts.length; i++) {
    const start = cuts[i], end = cuts[i + 1];
    const covering: SegmentMark[] = [];
    for (const r of ranges) {
      if (r.start > start || r.end < end || covering.some((m) => m.key === r.mark.key)) continue;
      covering.push({ key: r.mark.key, status: r.mark.status });
    }
    const text = body.slice(start, end);
    segments.push(covering.length
      ? { text, markKey: covering[0].key, status: covering[0].status, marks: covering }
      : { text });
  }
  return segments;
}

/** Whether pointing at or selecting the fact `key` activates this segment. */
export function segmentHasMark(segment: TextSegment, key: string | null | undefined): boolean {
  return key != null && (segment.marks?.some((m) => m.key === key) ?? false);
}

/** A segment's fact paths, one per line, as an attribute value: a mark is looked up by any of them. */
export function segmentMarkKeys(segment: TextSegment): string | undefined {
  return segment.marks?.map((m) => m.key).join("\n");
}

/**
 * Which neighbours continue the active fact's text. A quote that contains another fact's quote
 * is cut into several segments; the renderer joins the active ones so they read as one highlight.
 */
export function activeJoins(segments: TextSegment[], index: number, key: string | null | undefined): string | undefined {
  if (!segmentHasMark(segments[index], key)) return undefined;
  const joins = [
    index > 0 && segmentHasMark(segments[index - 1], key) && "before",
    index + 1 < segments.length && segmentHasMark(segments[index + 1], key) && "after",
  ].filter(Boolean).join(" ");
  return joins || undefined;
}

/** The marks relevant for one message: scoped ones for that message + unscoped ones. */
export function marksForMessage(
  marks: EvidenceMark[],
  messageId: string | null | undefined,
): EvidenceMark[] {
  return marks.filter((m) => !m.sourceMessageId || (messageId && m.sourceMessageId === messageId));
}

/** Unique marks by field path — the "what connects" rail (first occurrence wins). */
export function uniqueMarks(marks: EvidenceMark[]): EvidenceMark[] {
  const seen = new Set<string>();
  const out: EvidenceMark[] = [];
  for (const m of marks) {
    if (seen.has(m.key)) continue;
    seen.add(m.key);
    out.push(m);
  }
  return out;
}
